import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Laptop,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Sparkles,
  Server,
  Activity,
  X,
} from 'lucide-react';

import { useAppDispatch, useAppSelector } from '@app/hooks';
import { logout, selectCurrentUser } from '@features/auth/store/authSlice';
import { ROUTES } from '@constants/routes';
import { STORAGE_KEYS } from '@constants/storage';
import { cn } from '@utils/cn';

interface AdminSidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isMobileOpen: boolean;
  onCloseMobile: () => void;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({
  isCollapsed,
  onToggleCollapse,
  isMobileOpen,
  onCloseMobile,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector(selectCurrentUser);

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.AUTH_USER);
    dispatch(logout());
    navigate(ROUTES.LOGIN);
  };

  // Only the 2 requested menu items
  const navItems = [
    {
      path: ROUTES.ADMIN,
      label: 'Tổng quan hệ thống',
      shortLabel: 'Tổng quan',
      icon: LayoutDashboard,
      exact: true,
      badge: 'Live',
      badgeColor: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    },
    {
      path: ROUTES.ADMIN_APP_VERSIONS,
      label: 'Quản lý phiên bản app desktop',
      shortLabel: 'Phiên bản App',
      icon: Laptop,
      badge: 'AutoUpdate',
      badgeColor: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    },
  ];

  return (
    <>
      {/* Mobile Backdrop Overlay */}
      {isMobileOpen && (
        <div
          onClick={onCloseMobile}
          className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm lg:hidden"
        />
      )}

      {/* Main Left Sidebar Container */}
      <aside
        className={cn(
          'fixed left-0 top-0 bottom-0 z-50 flex flex-col border-r border-slate-800 bg-[#0c1322]/95 backdrop-blur-xl transition-all duration-300 ease-in-out',
          // Desktop Width
          isCollapsed ? 'lg:w-20' : 'lg:w-72',
          // Mobile Offcanvas behavior
          isMobileOpen
            ? 'translate-x-0 shadow-2xl shadow-black/80 w-72'
            : '-translate-x-full lg:translate-x-0',
        )}
      >
        {/* Top Brand Header */}
        <div className="flex h-16 items-center justify-between border-b border-slate-800/80 px-4">
          {!isCollapsed ? (
            <div className="flex items-center gap-2.5 overflow-hidden">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-500 shadow-md shadow-blue-500/20">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div className="flex flex-col truncate">
                <span className="truncate text-sm font-bold text-white tracking-tight">
                  PNR Admin Hub
                </span>
                <span className="truncate text-[11px] text-blue-400 font-medium">
                  Hệ thống Quản trị
                </span>
              </div>
            </div>
          ) : (
            <div className="mx-auto flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-500 shadow-md shadow-blue-500/20">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
          )}

          {/* Desktop Collapse/Expand Button */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onToggleCollapse}
              title={isCollapsed ? 'Mở rộng Menu' : 'Thu gọn Menu'}
              className="hidden lg:flex h-8 w-8 items-center justify-center rounded-lg border border-slate-700/60 bg-slate-800/40 text-slate-400 hover:bg-slate-700/60 hover:text-white transition-colors cursor-pointer"
            >
              {isCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </button>

            {/* Mobile Close Button */}
            <button
              type="button"
              onClick={onCloseMobile}
              className="flex lg:hidden h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* User Profile Card */}
        <div className="border-b border-slate-800/60 p-3.5">
          <div
            className={cn(
              'flex items-center rounded-xl border border-slate-800/80 bg-[#121b2f] p-2.5 transition-all',
              isCollapsed ? 'justify-center p-2' : 'gap-3',
            )}
          >
            <div className="relative shrink-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 text-sm font-bold text-white shadow-inner">
                {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[#121b2f] bg-emerald-500 animate-pulse" />
            </div>

            {!isCollapsed && (
              <div className="flex flex-1 flex-col overflow-hidden text-left">
                <span className="truncate text-xs font-semibold text-white">
                  {user?.name || 'Quản Trị Viên (Admin)'}
                </span>
                <span className="truncate text-[11px] text-slate-400">
                  {user?.email || 'admin@ngsxnc.vn'}
                </span>
                <div className="mt-1 flex items-center gap-1.5">
                  <span className="inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold bg-blue-500/20 text-blue-300 border border-blue-500/30">
                    PNR Client Admin
                  </span>
                  <span className="text-[10px] text-emerald-400 flex items-center gap-1">
                    <Activity className="h-2.5 w-2.5" /> Online
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Items (Only 2 items) */}
        <div className="flex-1 overflow-y-auto px-3 py-4 custom-scrollbar">
          <div className="mb-2 px-2">
            {!isCollapsed && (
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Menu Quản Trị
              </p>
            )}
          </div>

          <nav className="space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = item.exact
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path);

              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={onCloseMobile}
                  title={isCollapsed ? item.label : undefined}
                  className={cn(
                    'group relative flex w-full items-center rounded-xl px-3 py-2.5 text-xs font-medium transition-all duration-200 cursor-pointer',
                    isCollapsed ? 'justify-center' : 'justify-between',
                    isActive
                      ? 'bg-blue-600/15 text-blue-400 border border-blue-500/30 font-semibold shadow-sm shadow-blue-500/10'
                      : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200 border border-transparent',
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Icon
                      className={cn(
                        'h-4 w-4 shrink-0 transition-colors',
                        isActive
                          ? 'text-blue-400'
                          : 'text-slate-400 group-hover:text-slate-200',
                      )}
                    />
                    {!isCollapsed && (
                      <span className="truncate">{item.label}</span>
                    )}
                  </div>

                  {!isCollapsed && item.badge && (
                    <span
                      className={cn(
                        'rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                        item.badgeColor,
                      )}
                    >
                      {item.badge}
                    </span>
                  )}

                  {/* Active highlight indicator bar on the left */}
                  {isActive && (
                    <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r-full bg-blue-500" />
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* System status widget */}
          {!isCollapsed && (
            <div className="mt-8 rounded-xl border border-slate-800/80 bg-[#121b2f]/60 p-3">
              <div className="flex items-center justify-between text-[11px] text-slate-400">
                <div className="flex items-center gap-1.5">
                  <Server className="h-3.5 w-3.5 text-blue-400" />
                  <span>PNR Client Backend</span>
                </div>
                <span className="font-semibold text-emerald-400">Online</span>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-800">
                <div className="h-full w-[95%] rounded-full bg-gradient-to-r from-blue-500 to-emerald-400" />
              </div>
              <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500">
                <span>pnr-client.ngsxnc.vn</span>
                <span>v1.0 API</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-slate-800/80 p-3 space-y-1.5">
          <NavLink
            to={ROUTES.HOME}
            title={isCollapsed ? 'Về trang người dùng' : undefined}
            className={cn(
              'flex items-center rounded-xl px-3 py-2 text-xs font-medium text-slate-400 transition-colors hover:bg-slate-800/50 hover:text-white',
              isCollapsed ? 'justify-center' : 'gap-2.5',
            )}
          >
            <ExternalLink className="h-4 w-4 shrink-0 text-slate-400" />
            {!isCollapsed && <span>Xem trang chủ (Client)</span>}
          </NavLink>

          <button
            type="button"
            onClick={handleLogout}
            title={isCollapsed ? 'Đăng xuất' : undefined}
            className={cn(
              'flex w-full items-center rounded-xl px-3 py-2 text-xs font-medium text-red-400 transition-colors hover:bg-red-500/10 hover:text-red-300 border border-transparent hover:border-red-500/20 cursor-pointer',
              isCollapsed ? 'justify-center' : 'gap-2.5',
            )}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {!isCollapsed && <span>Đăng xuất tài khoản</span>}
          </button>
        </div>
      </aside>
    </>
  );
};
